import { Add, CreditCard, Face, Person } from '@mui/icons-material';
import { Box, Button, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { useAtomValue, useSetAtom } from 'jotai';
import * as React from 'react';
import { Route, useHistory, useLocation, useParams } from 'react-router-dom';
import { useFirestore } from '../hooks/useFirebase';
import { useStats } from '../hooks/useStats';
import {
  completeTagsJotaiAtom,
  loadingTeamMembersJotaiAtom,
  teamMemberIdsJotaiAtom,
  teamMembersJotaiAtom,
  teamMembersWriterJotaiAtom,
  teamsJotaiAtom
} from '../jotai/atoms';
import { analytics } from '../utils/analytics';
import { convertToMember } from '../utils/converters';
import { merge } from '../utils/merge';
import { DashboardAdmins } from './DashboardAdmins';
import { DashboardCreate } from './DashboardCreate';
import { DashboardMember } from './DashboardMember';
import { DashboardMembers } from './DashboardMembers';
import { DashboardPayments } from './DashboardPayments';
import { LoadingPage } from './LoadingPage';
import { TeamIcon } from './TeamIcon';

export type Params = {
  teamId: string;
  tab?: 'members' | 'admins' | 'payments' | 'create';
};

const styles = {
  root: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    overflow: 'hidden'
  },
  right: {
    flex: 1,
    borderLeftColor: 'divider',
    borderLeftStyle: 'solid',
    borderWidth: 1,
    display: 'flex',
    flexDirection: 'column'
  },
  scroll: {
    flex: 1,
    overflow: 'scroll',
    paddingBottom: 12.5
  },
  bar: {
    borderBottomColor: 'divider',
    borderBottomStyle: 'solid',
    borderWidth: 1
  },
  notFound: {
    padding: 3,
    '& > *': {
      marginBottom: 3
    }
  },
  tab: {
    flexDirection: 'row',
    '& .MuiTab-iconWrapper': {
      marginTop: '6px'
    }
  }
} as const;

export function Dashboard({}) {
  // 現在表示しようとしているチームを取得する
  const { teamId, tab } = useParams<Params>();
  const team = useAtomValue(teamsJotaiAtom(teamId)); // 現在表示しようとしているチーム
  const isEmpty = useAtomValue(teamMemberIdsJotaiAtom(teamId)).length === 0;

  // チームメンバーをクエリする
  const firestore = useFirestore();
  const setLoading = useSetAtom(loadingTeamMembersJotaiAtom(teamId));
  const setMemberIds = useSetAtom(teamMemberIdsJotaiAtom(teamId));
  const setCompleteTags = useSetAtom(completeTagsJotaiAtom(teamId));
  const writeMembers = useSetAtom(teamMembersWriterJotaiAtom(teamId));

  React.useEffect(() => {
    setLoading(true);
    // チームに所属しているメンバーの情報を全て取得する
    const unsubscribe = firestore
      .collection('teams')
      .doc(teamId)
      .collection('members')
      .withConverter(convertToMember)
      .onSnapshot(qs => {
        const updates: { uid: string; member: IMember | undefined }[] = [];
        const uids: string[] = [];
        // データを格納する
        qs.forEach(ds => {
          const member = ds.data();
          updates.push({ uid: ds.id, member });
          setCompleteTags(curr => merge(curr, member.tags));
          uids.push(ds.id);
        });
        writeMembers(updates);
        // uid の配列をマージする
        setMemberIds(curr => merge(curr, uids));
        // ローディングインジケータを止める
        setLoading(false);
      });
    return () => {
      unsubscribe();
    };
  }, [
    teamId,
    firestore,
    setLoading,
    setMemberIds,
    setCompleteTags,
    writeMembers
  ]);

  const history = useHistory();
  const changeTab = React.useCallback((e: any, value: string) => {
    analytics.changeTab(value);
    history.push(`/${teamId}/${value}`);
  }, []);

  // スクロールを戻す
  const location = useLocation();
  const scrollerRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    scrollerRef.current?.scrollTo(0, 0);
  }, [location.pathname]);

  if (!team) {
    return (
      <Box sx={styles.notFound}>
        <Typography variant="h6">
          URL が間違っているか、存在しないチームです
        </Typography>
        <Button color="primary" onClick={() => history.push('/')}>
          トップに戻る
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={styles.root}>
      <TeamInfo />
      <Box sx={styles.right}>
        <Tabs
          value={tab}
          indicatorColor="primary"
          textColor="primary"
          sx={styles.bar}
          onChange={changeTab}
        >
          <Tab
            value="members"
            icon={<Face />}
            sx={styles.tab}
            label="メンバー"
            data-testid="tab-members"
          />
          <Tab
            value="admins"
            icon={<Person />}
            sx={styles.tab}
            label="管理者"
            data-testid="tab-admins"
          />
          <Tab
            value="payments"
            icon={<CreditCard />}
            sx={styles.tab}
            label={
              <Tooltip
                open={team?.enabled === false && tab !== 'payments'}
                arrow
                disableHoverListener
                placement="top"
                title="お支払い情報を追加いただくと、今すぐ利用を開始できます"
              >
                <span>お支払い情報</span>
              </Tooltip>
            }
            data-testid="tab-payments"
          />
          <Tab
            value="create"
            icon={<Add />}
            sx={styles.tab}
            label={
              <Tooltip
                open={Boolean(team?.enabled && isEmpty && tab !== 'create')}
                arrow
                disableHoverListener
                placement="top"
                title="こちらで一人目のメンバー（生徒）を追加できます"
              >
                <span>メンバー追加</span>
              </Tooltip>
            }
            data-testid="tab-create"
          />
        </Tabs>
        <Box sx={styles.scroll} ref={scrollerRef}>
          <React.Suspense fallback={<LoadingPage />}>
            <Route
              exact
              path="/:teamId?/members"
              component={DashboardMembers}
            />
            <Route path="/:teamId?/members/:uid" component={DashboardMember} />
            <Route path="/:teamId?/admins" component={DashboardAdmins} />
            <Route path="/:teamId?/payments" component={DashboardPayments} />
            <Route path="/:teamId?/create" component={DashboardCreate} />
          </React.Suspense>
        </Box>
      </Box>
    </Box>
  );
}

const infoStyles = {
  left: {
    padding: 3,
    display: 'flex',
    flexDirection: 'column'
  },
  teamName: {
    marginTop: 0.5,
    marginBottom: 4
  },
  column: {
    display: 'flex',
    flexDirection: 'row',
    minWidth: 200,
    '& > *': {
      width: '50%',
      paddingBottom: 2
    }
  }
} as const;

function TeamInfo() {
  // 現在表示しようとしているチーム
  const { teamId } = useParams<Params>();
  const team = useAtomValue(teamsJotaiAtom(teamId)); // 現在表示しようとしているチーム

  // 現在の席数など
  const { sum, has } = useStats(teamId);

  return (
    <Box sx={infoStyles.left}>
      <TeamIcon id={teamId} />
      <Typography variant="h6" sx={infoStyles.teamName}>
        {team?.name}
      </Typography>
      <Box>
        <Box sx={infoStyles.column}>
          <Typography variant="body1" color="text.secondary">
            メンバー数
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {sum || '-'}
          </Typography>
        </Box>
        <Box sx={infoStyles.column}>
          <Typography variant="body1" color="text.secondary">
            プラン
          </Typography>
          <Typography variant="body1" color="text.secondary">
            有料（法人）
          </Typography>
        </Box>
        <Box sx={infoStyles.column}>
          <Typography variant="body1" color="text.secondary">
            有効人数
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {typeof has === 'number' ? has : ''}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
